import React, {
	KeyboardEvent,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import ReactAsyncSelect from 'react-select/async';
import { FieldError } from 'react-hook-form';
import {
	ActionMeta,
	InputActionMeta,
	SingleValue,
	StylesConfig,
} from 'react-select';
import useTranslation from '../../../Hooks/useTranslation';
import { useLazyTermItemsQuery } from '../../../graphql/graphql';
import { SelectValue } from '../Select/Select';
import styles from '../Select/Select.module.scss';

interface DiagnosisSelectProps {
	onChange?: (value: SelectValue | null) => void;
	value?: SelectValue | null;
	placeholder?: string;
	portal?: boolean;
	error?: FieldError;
	isClearable?: boolean;
	isSearchable?: boolean;
}

const DiagnosisSelectSpecial: React.FC<DiagnosisSelectProps> = ({
	onChange,
	value,
	placeholder,
	portal = true,
	error,
	isClearable = true,
	isSearchable = true,
}) => {
	const { t } = useTranslation();
	const [getTermItems] = useLazyTermItemsQuery();

	const [inputValue, setInputValue] = useState('');
	const [isEditing, setIsEditing] = useState(false);
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const components = useMemo(
		() => ({
			DropdownIndicator: () => null,
			IndicatorSeparator: () => null,
		}),
		[],
	);

	const loadOptions = (searchedValue: string): Promise<SelectValue[]> => {
		if (!searchedValue.trim()) return Promise.resolve([]);

		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		return new Promise(resolve => {
			searchTimeoutRef.current = setTimeout(async () => {
				try {
					const { termItems } = await getTermItems({
						termType: 'diagnosis',
						fullText: searchedValue,
						take: 20,
					}).unwrap();

					resolve(
						(termItems?.data || [])
							.filter(t => t.name)
							.map(t => ({
								label: `${t.value} - ${t.name}`,
								value: t.value ?? t.id,
							})),
					);
				} catch {
					resolve([]);
				}
			}, 500);
		});
	};

	const handleChange = (
		newValue: SingleValue<SelectValue>,
		_actionMeta: ActionMeta<SelectValue>,
	) => {
		if (!newValue) {
			setInputValue('');
			setIsEditing(false);
			onChange?.(null);
			return;
		}

		setInputValue('');
		setIsEditing(false);
		onChange?.(newValue);
	};

	const handleInputChange = (
		newInputValue: string,
		meta: InputActionMeta,
	): string => {
		if (meta.action === 'input-change') {
			setInputValue(newInputValue);

			if (value && newInputValue !== value.label) {
				onChange?.(null);
			}

			return newInputValue;
		}

		return inputValue;
	};

	const handleFocus = () => {
		if (value) {
			setIsEditing(true);
			setInputValue(value.label);
		} else {
			setIsEditing(true);
		}
	};

	const handleBlur = () => {
		if (value) {
			setInputValue('');
			setIsEditing(false);
		} else if (!inputValue) {
			setIsEditing(false);
		}
	};

	const handleKeyDown = (event: KeyboardEvent) => {
		if (!value || isEditing) return;

		if (event.key === 'Backspace' || event.key === 'Delete') {
			event.preventDefault();

			setIsEditing(true);
			setInputValue(value.label.slice(0, -1));
			onChange?.(null);
		}
	};

	useEffect(() => {
		if (isEditing) return;

		if (!value) {
			setInputValue('');
			setIsEditing(false);
		}
	}, [value, isEditing]);

	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
		};
	}, []);

	const selectStyles: StylesConfig<SelectValue, false> = {
		menuPortal: base => ({
			...base,
			zIndex: 9999,
		}),
		...(error
			? {
					control: base => ({
						...base,
						borderColor: 'red',
						boxShadow: 'none',
						'&:hover': { borderColor: 'red' },
					}),
			  }
			: {}),
	};

	return (
		<div className={styles.wrapper}>
			<ReactAsyncSelect
				classNamePrefix='reactSearchSelect'
				cacheOptions={false}
				defaultOptions={false}
				loadOptions={loadOptions}
				value={isEditing ? null : value}
				inputValue={isEditing ? inputValue : ''}
				onInputChange={handleInputChange}
				onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				placeholder={placeholder || t('base.shared.pick')}
				noOptionsMessage={() => t('base.startTyping')}
				isClearable={isClearable}
				isSearchable={isSearchable}
				components={components}
				menuPortalTarget={portal ? document.body : undefined}
				blurInputOnSelect={false}
				openMenuOnFocus
				backspaceRemovesValue={false}
				styles={selectStyles}
			/>
		</div>
	);
};

export default DiagnosisSelectSpecial;
